import Loading from '@doordash/component-loading'
import { Colors, Spacing } from '@doordash/design-language'
import * as React from 'react'
import { useEffect } from 'react'
import { connect } from 'react-redux'
import { get } from 'lodash'
import styled from 'styled-components'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as Exp from '../../../experimentConstants'
import {
  getOnboardingRequest,
  login as loginRequest,
  UpdateOnboardingOptions,
  updateOnboardingRequest,
} from '../../../actions'
import { WorkflowError } from '../../../components/organisms/workflow-template/WorkflowError'
import {
  trackWorkflowStepRendered,
  WorkflowTrackingProps,
} from '../../../events'
import { RootState } from '../../../reducers'
import { WorkflowTemplate } from '../../organisms/workflow-template'
import {
  TemplateResponseData,
  WorkflowTemplate as WorkflowTemplateType,
} from '../../organisms/workflow-template/types'
import { SupportedCountryCode } from '../../../models/types'
import routes from '../../../routes'
import useScheduleAction from '../../../hooks/useScheduleAction'

export type WorkflowErrorType = {
  message: string
  status: number // http status code
  type: string
}

type IOnboardingWorkflow = {
  /**
   *  applicant email for dasher onboarding
   */
  email: string
  /**
   * unique applicant link for dasher onboarding
   */
  uniqueLink: string
  /**
   * country code for dasher onboarding
   */
  signupCountryCode: SupportedCountryCode
  /**
   * current step of dasher onboarding user is on
   */
  currentStep: string
  /**
   * UI template returned from BE
   */
  template: WorkflowTemplateType
  /**
   * loading flag for step
   */
  isLoading: boolean
  /**
   * all steps complete flag
   */
  stepsComplete: boolean
  /**
   * BE API error to be shown to user
   */
  error: WorkflowErrorType
  /**
   * action for getting onboarding object
   */
  getOnboarding: (uniqueLink: string, countryCode: SupportedCountryCode) => void
  /**
   * action to submit and go to next/update onboarding
   */
  updateOnboarding: typeof updateOnboardingRequest

  login: typeof loginRequest

  trackWorkflowStepRendered: (trackingData: WorkflowTrackingProps) => void
}

export const BannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Spacing.XxxSmall}px;
  background-color: ${Colors.BannerNegativeBackground};
`
export type SubmitOnboardingCallback = (
  data: TemplateResponseData,
  options?: UpdateOnboardingOptions
) => void

const switchBackToNonPlatformFlow = uniqueLink => {
  window.location.replace(routes.dasherApplication.buildPath({ uniqueLink }))
}

const OnboardingWorkflow = ({
  email,
  template,
  uniqueLink,
  currentStep,
  isLoading,
  error,
  stepsComplete,
  signupCountryCode,
  login,
  getOnboarding,
  updateOnboarding,
  trackWorkflowStepRendered,
}: IOnboardingWorkflow) => {
  useEffect(() => {
    getOnboarding(uniqueLink, signupCountryCode)
  }, [getOnboarding, uniqueLink, signupCountryCode])

  const currentPageTemplate = template.pages.find(
    page => page.step_id === currentStep
  )

  useScheduleAction({
    currentPageTemplate,
    uniqueLink,
    template,
    currentStep,
    signupCountryCode,
    getOnboarding,
    updateOnboarding,
    error,
  })

  const onSubmitStep: SubmitOnboardingCallback = React.useCallback(
    async (
      data: TemplateResponseData,
      options: UpdateOnboardingOptions = {}
    ) => {
      await updateOnboarding(
        uniqueLink,
        data,
        currentPageTemplate,
        {
          workflowId: template.template_id,
          stepName: currentPageTemplate.step_name || currentStep,
          uniqueLink,
          pageId: currentPageTemplate.page_id,
        },
        options
      )

      /**
       * TODO: remove hardcoded references to template fields
       */
      if (
        options.login &&
        data.INPUT_TYPE_NAMES_AND_PASSWORD &&
        data.INPUT_TYPE_NAMES_AND_PASSWORD.json_data
      ) {
        const { password } = data.INPUT_TYPE_NAMES_AND_PASSWORD.json_data
        login(
          email,
          password,
          uniqueLink,
          isFeatureFlagEnabled(Exp.DASHER_WEB_IDENTITY_SERVICE_MIGRATION),
          true
        )
      }
    },
    [
      currentPageTemplate,
      email,
      uniqueLink,
      currentStep,
      template.template_id,
      login,
      updateOnboarding,
    ]
  )

  const reload = () => {
    getOnboarding(uniqueLink, signupCountryCode)
  }

  // noReload indicates the previous template is identical to the current one, no loading is needed
  const noReload = get(currentPageTemplate, 'stop_reload', false)

  if (!noReload && isLoading) {
    return <Loading />
  }

  if (error || !currentPageTemplate) {
    return <WorkflowError error={error} onRetry={reload} />
  }
  // When steps complete, step ID from BE is fixed
  if (stepsComplete) {
    // No more steps in the workflow of Dx onboarding platform? Switch back to non-platform flow
    switchBackToNonPlatformFlow(uniqueLink)
  }

  return (
    <WorkflowTemplate
      currentPageTemplate={currentPageTemplate}
      onSubmitStep={onSubmitStep}
      trackWorkflowStepRendered={() =>
        trackWorkflowStepRendered({
          workflowId: template.template_id,
          stepName: currentPageTemplate.step_name || currentStep,
          uniqueLink,
          pageId: currentPageTemplate.page_id,
        })
      }
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  email: state.application.email,
  uniqueLink: state.application.uniqueLink,
  signupCountryCode: state.signupCountryShortname,
  template: state.onBoardingWorkflow.template,
  error: state.onBoardingWorkflow.error,
  isLoading: state.onBoardingWorkflow.isLoading,
  currentStep: state.onBoardingWorkflow.currentStep,
  stepsComplete: state.onBoardingWorkflow.stepsComplete,
})

const mapDispatchToProps = {
  getOnboarding: getOnboardingRequest,
  updateOnboarding: updateOnboardingRequest,
  login: loginRequest,
  trackWorkflowStepRendered,
}

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingWorkflow)
